import { useState, useEffect, useRef, useMemo } from 'react'
import KnurledWheel from './KnurledWheel'

const EXPIRY_OPTIONS = [
  { label: 'Never', value: 0 },
  { label: '1h', value: 3600 },
  { label: '4h', value: 14400 },
  { label: '24h', value: 86400 },
  { label: '7d', value: 604800 },
]

const SLIPPAGE_OPTIONS = [
  { label: 'Exact', value: 0 },
  { label: '0.5%', value: 50 },
  { label: '1%', value: 100 },
  { label: '3%', value: 300 },
]

function OrderModal({
  show, side, mcap, formatMcap, onClose, onConfirm, showToast, tokenBalance, currentMcap,
  // ─── Futures props (optional) ───
  dataSource = 'solana',
  hlPair = null,
  defaults = {},
  positions = [],
}) {
  const isFutures = dataSource === 'hyperliquid' && hlPair
  const [amount, setAmount] = useState(() => localStorage.getItem('solcheddar_buyAmount') || '')
  const [percentage, setPercentage] = useState(null)
  const [expiry, setExpiry] = useState(0)
  const [slippageBps, setSlippageBps] = useState(0)
  const [ocoEnabled, setOcoEnabled] = useState(false)
  const [ocoMcap, setOcoMcap] = useState('')
  const [ocoPercentage, setOcoPercentage] = useState(null)
  const inputRef = useRef(null)

  // ─── Futures state ───
  const [futuresQty, setFuturesQty] = useState('')
  const [orderMode, setOrderMode] = useState('limit')

  useEffect(() => {
    if (show && inputRef.current) {
      setPercentage(null)
      setOcoEnabled(false)
      setOcoMcap('')
      setOcoPercentage(null)
      if (isFutures) {
        setFuturesQty(String(defaults.futuresQty || '0.01'))
        setOrderMode('limit')
      }
      if (side === 'buy') {
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
  }, [show, side, isFutures, defaults.futuresQty])

  const handleConfirm = () => {
    const options = {}
    if (slippageBps > 0) options.slippageBps = slippageBps
    if (expiry > 0) options.expiredAt = Math.floor(Date.now() / 1000) + expiry

    if (side === 'buy') {
      const value = parseFloat(amount)
      if (!value || value <= 0) {
        showToast('Enter a valid amount', 'sell')
        return
      }
      localStorage.setItem('solcheddar_buyAmount', amount)
      onConfirm({ type: 'sol', amount: value, options })
    } else {
      if (!percentage) {
        showToast('Select a percentage', 'sell')
        return
      }
      // OCO mode: include TP + SL data
      if (ocoEnabled) {
        const ocoMcapVal = parseFloat(ocoMcap)
        if (!ocoMcapVal || ocoMcapVal <= 0) {
          showToast('Enter OCO target mcap', 'sell')
          return
        }
        if (!ocoPercentage) {
          showToast('Select OCO sell percentage', 'sell')
          return
        }
        // Determine which is TP and which is SL based on current mcap
        const primaryMcap = mcap
        const secondaryMcap = ocoMcapVal
        const isTPAbove = primaryMcap > currentMcap
        onConfirm({
          type: 'oco',
          tpMcap: isTPAbove ? primaryMcap : secondaryMcap,
          tpPercentage: isTPAbove ? percentage : ocoPercentage,
          slMcap: isTPAbove ? secondaryMcap : primaryMcap,
          slPercentage: isTPAbove ? ocoPercentage : percentage,
          options,
        })
      } else {
        onConfirm({ type: 'percentage', percentage, options })
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm()
    }
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!show) return null

  // ═══════════════════════════════════════════════════════════
  // FUTURES MODAL (Hyperliquid)
  // ═══════════════════════════════════════════════════════════
  if (isFutures) {
    const formatPrice = (p) => {
      if (!p || p === 0) return '$0'
      if (p >= 1000) return '$' + p.toLocaleString(undefined, { maximumFractionDigits: 2 })
      if (p >= 1) return '$' + p.toFixed(2)
      return '$' + p.toPrecision(4)
    }

    const notional = parseFloat(futuresQty || 0) * mcap
    const margin = notional / (defaults.futuresLeverage || 10)

    const handleFuturesConfirm = () => {
      const qty = parseFloat(futuresQty)
      if (!qty || qty <= 0) { showToast('Enter a valid quantity', 'sell'); return }
      onConfirm({
        type: 'futures',
        amount: qty,
        side,
        orderMode,
        options: {},
      })
    }

    const confirmText = orderMode === 'market'
      ? `Market ${side === 'buy' ? 'Buy' : 'Sell'}`
      : `Limit ${side === 'buy' ? 'Buy' : 'Sell'}`

    return (
      <div className={`modal-overlay ${show ? 'show' : ''}`} onClick={handleOverlayClick}>
        <div className="order-modal">
          <style>{`
            .no-spinner::-webkit-outer-spin-button,
            .no-spinner::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
            .no-spinner { -moz-appearance: textfield; appearance: textfield; }
          `}</style>
          <h2 className={side}>{side === 'buy' ? 'BUY' : 'SELL'}</h2>

          {/* Price bar */}
          <div className="mcap-display" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Price: <span>{formatPrice(mcap)}</span></span>
            <span style={{ fontSize: '11px', color: '#666', fontFamily: 'monospace' }}>
              {hlPair.base}/USDC · {defaults.futuresLeverage || 10}x
            </span>
          </div>

          {/* Market / Limit toggle */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
            <button onClick={() => setOrderMode('market')}
              style={{
                flex: 1, textAlign: 'center', padding: '4px 0', fontSize: '11px', fontWeight: 600,
                background: orderMode === 'market' ? '#1a3a1a' : '#0a0a15',
                color: orderMode === 'market' ? '#00ff55' : '#666',
                border: orderMode === 'market' ? '1px solid #00ff55' : '1px solid #2a2a40',
                borderRadius: '3px', cursor: 'pointer', fontFamily: 'monospace',
              }}>MARKET</button>
            <button onClick={() => setOrderMode('limit')}
              style={{
                flex: 1, textAlign: 'center', padding: '4px 0', fontSize: '11px', fontWeight: 600,
                background: orderMode === 'limit' ? '#1a2a3a' : '#0a0a15',
                color: orderMode === 'limit' ? '#4488ff' : '#666',
                border: orderMode === 'limit' ? '1px solid #4488ff' : '1px solid #2a2a40',
                borderRadius: '3px', cursor: 'pointer', fontFamily: 'monospace',
              }}>LIMIT</button>
          </div>

          {/* Qty input */}
          <label>Quantity ({hlPair.base})</label>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: '8px' }}>
            <input
              ref={inputRef}
              className="no-spinner"
              type="number"
              value={futuresQty}
              onChange={(e) => setFuturesQty(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleFuturesConfirm() }}
              placeholder="0.01"
              step="0.001"
              min="0"
              style={{ flex: 1 }}
            />
            <KnurledWheel
              value={parseFloat(futuresQty) || 0}
              onChange={(v) => setFuturesQty(String(v))}
              step={0.001}
              min={0}
              width={34}
              style={{ height: 'auto' }}
              ariaLabel="Quantity"
            />
          </div>

          {/* Notional / margin info */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px', fontFamily: 'monospace', color: '#888' }}>
            <span>Notional: {formatPrice(notional)}</span>
            <span>Margin: {formatPrice(margin)}</span>
          </div>

          <div className="btn-row">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className={`btn-confirm ${side}`} onClick={handleFuturesConfirm}>
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════
  // SOLANA MODAL (original)
  // ═══════════════════════════════════════════════════════════

  return (
    <div className={`modal-overlay ${show ? 'show' : ''}`} onClick={handleOverlayClick}>
      <div className="order-modal">
        <h2 className={side}>{side.toUpperCase()}</h2>
        <div className="mcap-display">
          Target MCap: <span>{formatMcap(mcap)}</span>
        </div>
        
        {side === 'buy' ? (
          <>
            <label>Amount (SOL)</label>
            <input
              ref={inputRef}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </>
        ) : (
          <>
            <label>Sell Amount</label>
            {tokenBalance > 0 && (
              <div className="token-balance">
                Balance: {tokenBalance.toLocaleString()} tokens
              </div>
            )}
            <div className="percentage-buttons">
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  className={`pct-btn ${percentage === pct ? 'selected' : ''}`}
                  onClick={() => setPercentage(pct)}
                >
                  {pct}%
                </button>
              ))}
            </div>

            {/* OCO Toggle */}
            <div className="oco-section">
              <label className="oco-toggle">
                <input
                  type="checkbox"
                  checked={ocoEnabled}
                  onChange={(e) => setOcoEnabled(e.target.checked)}
                />
                <span>OCO (TP + SL)</span>
              </label>
              {ocoEnabled && (
                <div className="oco-fields">
                  <label>Other Side MCap (SOL)</label>
                  <input
                    type="number"
                    value={ocoMcap}
                    onChange={(e) => setOcoMcap(e.target.value)}
                    placeholder={mcap > currentMcap ? 'Stop Loss mcap...' : 'Take Profit mcap...'}
                    step="0.1"
                    min="0"
                  />
                  <label>Sell %</label>
                  <div className="percentage-buttons">
                    {[25, 50, 75, 100].map((pct) => (
                      <button
                        key={`oco-${pct}`}
                        className={`pct-btn ${ocoPercentage === pct ? 'selected' : ''}`}
                        onClick={() => setOcoPercentage(pct)}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Advanced Options */}
        <div className="order-advanced">
          <div className="advanced-row">
            <label>Expiry</label>
            <div className="option-pills">
              {EXPIRY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`pill ${expiry === opt.value ? 'active' : ''}`}
                  onClick={() => setExpiry(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="advanced-row">
            <label>Slippage</label>
            <div className="option-pills">
              {SLIPPAGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`pill ${slippageBps === opt.value ? 'active' : ''}`}
                  onClick={() => setSlippageBps(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="btn-row">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className={`btn-confirm ${side}`} onClick={handleConfirm}>
            {ocoEnabled ? 'Place OCO' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default OrderModal